import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { AxiosError } from 'axios'
import { ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { Toast } from '@/common/components/Toast'
import { OrderBy, useOrderBy } from '@/common/hooks/useOrderBy'
import { applicationModel } from '@/common/models/application'
import { calculateItemsPerPage } from '@/common/utils/calculateItemsPerPage'
import { api, ApiError, APIPaginated, ApiWhere } from '@/lib/axios'
import { FilterApplicationsForm } from '../schemas/FilterApplicationsSchema'
import { useFilterApplications } from './useFilterApplications'

export const applicationPaginatedDTO = applicationModel

export type ApplicationPaginated = z.infer<typeof applicationPaginatedDTO>

export type GetPaginateApplicationsProps = {
  page: number
  orderBy: OrderBy
  filters: FilterApplicationsForm
}

async function getPaginateApplications(props: GetPaginateApplicationsProps) {
  const { filters } = props

  const where: ApiWhere = {
    isActive: !filters.isActive,
    ...(filters.tag && { tag: { array_contains: [filters.tag] } }),
    ...(filters.requiresApiUser && {
      requiresApiUser: filters.requiresApiUser,
    }),
    ...(filters.requiresEntityAccount && {
      requiresEntityAccount: filters.requiresEntityAccount,
    }),
    ...(filters.freeSearch && {
      AND: [
        {
          OR: [
            { title: { contains: filters.freeSearch } },
            { tag: { array_contains: [filters.freeSearch] } },
          ],
        },
      ],
    }),
  }

  const { data } = await api.backend.get<APIPaginated<ApplicationPaginated>>(
    '/application/paginate',
    {
      params: {
        page: props.page,
        limit: 25,
        relations: {
          orderBy: props.orderBy,
          where,
        },
      },
    },
  )

  return data
}

export const usePaginateApplications = () => {
  const [tableSearch, setTableSearch] = useState('')
  const [tableSearchResult, setTableSearchResult] = useState<
    ApplicationPaginated[]
  >([])
  const [page, setPage] = useState(1)
  const [filters] = useFilterApplications()

  const { t } = useTranslation(['common', 'sso'])
  const debouncedTableSearch = useDebounce(tableSearch, 300)
  const { orderBy, handleChangeOrder } = useOrderBy()
  const { data, isLoading, error } = useQuery<
    APIPaginated<ApplicationPaginated>,
    AxiosError<ApiError>
  >({
    queryKey: [
      '/application/paginate',
      page,
      orderBy,
      ...Object.values(filters),
    ],
    queryFn: () => getPaginateApplications({ page, orderBy, filters }),
  })

  const handleApplicationsSearch = (search: string) => {
    setTableSearch(search?.toLowerCase() || '')
  }

  const handleChangePage = (page: number) => {
    setPage(page)
  }

  const applicationsBadges = useMemo(() => {
    return {
      applicationKey: new Set(
        data?.items?.map((register) => register.applicationKey) || [],
      ).size.toFixed(0),
    }
  }, [data])

  useEffect(() => {
    if (!error) return

    toast(Toast, {
      data: {
        type: 'danger',
        icon: ServerCrash,
        title: t('sso:applications.paginate_table.toast_error.title'),
        description:
          error?.response?.data?.friendlyMessage ||
          t('sso:applications.paginate_table.toast_error.description'),
      },
    })
  }, [error, t])

  useEffect(() => {
    if (!data || data?.items?.length === 0) return

    const filteredData = data.items.filter(
      (application) =>
        application.title?.toLowerCase().includes(debouncedTableSearch) ||
        application.tag?.some((tag) =>
          tag?.toLowerCase().includes(debouncedTableSearch),
        ),
    )

    setTableSearchResult(filteredData)
  }, [data, debouncedTableSearch])

  return {
    applications: debouncedTableSearch ? tableSearchResult : data?.items || [],
    applicationsPaginated: data?.meta,
    isLoadingApplications: isLoading,
    applicationsOrderBy: orderBy,
    applicationsBadges,
    applicationsPerPage: calculateItemsPerPage(data?.meta),
    filtersQty: Object.values(filters).filter(Boolean).length,
    handleApplicationsSearch,
    handleChangePage,
    handleChangeOrder,
  }
}
